/**
 * "Where this objective lives": the other expressions of one Objective in
 * Priorities.ai, and the records in other systems it is linked to and kept in
 * step with (a Linear initiative, a Jira issue). Drawn in the Objectives page's
 * open row. Canon 01 › Strategic Objectives (one objective, many expressions)
 * and › Field Source, Sync Direction and Conflict Rule.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EXPRESSION_LABELS, objectiveExpressions, type ObjectiveExpression } from '../../../lib/strategyObjectLinks';
import { LinkedElsewhere } from './LinkedElsewhere';

export function ObjectiveWhereItLives({ workspaceId, objective }: {
  workspaceId: string;
  objective: { id: string; name: string; description?: string | null };
}) {
  const [expressions, setExpressions] = useState<ObjectiveExpression[] | null>(null);

  useEffect(() => {
    let live = true;
    objectiveExpressions(workspaceId, objective.id).then((e) => live && setExpressions(e)).catch(() => live && setExpressions([]));
    return () => { live = false; };
  }, [workspaceId, objective.id]);

  return (
    <div className="px-10 py-3 space-y-3 border-t border-[var(--rule)]">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">Where this objective lives</p>

      <div className="space-y-1">
        <p className="text-xs font-medium text-[var(--text-sec)]">In Priorities.ai</p>
        {expressions === null ? (
          <p className="text-xs text-[var(--text-ter)]">Reading…</p>
        ) : expressions.length === 0 ? (
          <p className="text-xs text-[var(--text-ter)]">No V2MOM line, OKR, Goal or Desired Outcome points at it yet.</p>
        ) : (
          <ul className="space-y-0.5">
            {expressions.map((e) => (
              <li key={`${e.kind}:${e.id}`} className="text-xs">
                <span className="text-[var(--text-ter)]">{EXPRESSION_LABELS[e.kind]}: </span>
                <Link to={e.href} className="text-[var(--text-pri)] hover:text-primary-600">{e.title || 'Untitled'}</Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <LinkedElsewhere
        workspaceId={workspaceId}
        type="objective"
        id={objective.id}
        values={{ name: objective.name, description: objective.description ?? '' }}
      />
    </div>
  );
}
